import {Component, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {NzSelectModule} from 'ng-zorro-antd/select';
import {FormsModule} from '@angular/forms';
import {CommonModule} from '@angular/common';
import {NzSwitchModule} from 'ng-zorro-antd/switch';
import {NzIconModule} from 'ng-zorro-antd/icon';
import {NzButtonModule} from 'ng-zorro-antd/button';
import {AuthService} from '../services/auth.service';
import {de_DE, en_US, NzI18nService} from 'ng-zorro-antd/i18n';
import {CompanyService} from '../services/company.service';
import {NzTooltipDirective} from 'ng-zorro-antd/tooltip';
import {NzDrawerModule} from 'ng-zorro-antd/drawer';
import {NzImage, NzImageService} from 'ng-zorro-antd/image';
import {HelpCenterDrawerComponent} from '../help-center/help-center-drawer.component';
import {NotificationCenterComponent} from '../notification-center/notification-center.component';
import { TourActionRegistryService } from '../services/tour-action-registry.service';
import { UserTourService } from '../services/user-tour.service';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [
    NzSelectModule,
    FormsModule,
    CommonModule,
    NzSwitchModule,
    NzIconModule,
    NzButtonModule,
    TranslateModule,
    NzTooltipDirective,
    NzDrawerModule,
    HelpCenterDrawerComponent,
    NotificationCenterComponent
  ],
  providers: [NzImageService],
  templateUrl: './header.component.html',
  styleUrl: './header.component.less'
})
export class HeaderComponent implements OnInit, OnDestroy {
  selectedLanguage = 'de';
  languages = [
    { value: 'de', label: 'Deutsch', flag: 'de' },
    { value: 'en', label: 'English', flag: 'gb' }
  ];

  // Map language -> flag code for the closed-face icon
  private flagByLang: Record<string, string> = {
    de: 'de',
    en: 'gb'
  };

  get selectedFlag(): string {
    return this.flagByLang[this.selectedLanguage] || 'de';
  }

  @Input() theme: boolean = false;  // Theme input property
  @Output() themeChange = new EventEmitter<boolean>();  // Theme change output property
  userProfilePic: string = 'assets/caretaker-example.jpg';
  userEmail: string = '';
  userName: string = '';
  isHelpDrawerVisible = false;
  drawerWidth: string = '480px';
  private productTourLaunchPending = false;
  private readonly helpDrawerWrapperSelector = '.scanafy-help-drawer';
  private helpDrawerCloseObserver: MutationObserver | null = null;
  private helpDrawerTransitionTarget: HTMLElement | null = null;
  private helpDrawerCloseTransitionCompleted = false;
  private unregisterTourAction: (() => void) | null = null;
  get showNotificationCenter(): boolean { return this.authService.isCurrentCompanyUser(); }

  constructor(
    private translate: TranslateService,
    private authService: AuthService,
    private nzI18nService: NzI18nService,
    private companyService: CompanyService,
    private nzImageService: NzImageService,
    private tourActions: TourActionRegistryService,
    private userTourService: UserTourService
  ) {
    // Retrieve the saved language from local storage or use default 'en'
    const savedLanguage = localStorage.getItem('selectedLanguage') || 'de';
    const savedTheme = localStorage.getItem('theme') === 'true';

    this.selectedLanguage = savedLanguage;

    this.theme = savedTheme;

    if (savedLanguage === 'en') {
      this.nzI18nService.setLocale(en_US);
    } else if (savedLanguage === 'de') {
      this.nzI18nService.setLocale(de_DE);
    }

    this.translate.setDefaultLang(savedLanguage);
    this.translate.use(savedLanguage);

    // Decode token and check user role
    const token = this.authService.getToken(); // Get the JWT token from storage
    if (token) {
      const decodedToken = this.authService.decodeToken(token); // Decode the token
      const userRole = decodedToken?.role || ''; // Extract the role from the token
      this.userEmail = decodedToken?.email || '';
      if (userRole === 'User') {
        // Only load profile picture if the role is 'User'
        this.loadProfilePicture();
      }
    }
  }

  ngOnInit(): void {
    this.updateDrawerWidth();
    this.unregisterTourAction = this.tourActions.register('header.open-help', () => {
      this.openHelpDrawer();
      return { close: () => this.closeHelpDrawer() };
    });
  }

  ngOnDestroy(): void {
    this.clearHelpDrawerCloseWait();
    this.productTourLaunchPending = false;
    this.unregisterTourAction?.();
    this.unregisterTourAction = null;
  }

  // Switch language and save to local storage
  switchLanguage(lang: string) {
    this.translate.use(lang);
    localStorage.setItem('selectedLanguage', lang);
    // Update Ant Design locale
    if (lang === 'en') {
      this.nzI18nService.setLocale(en_US);
    } else if (lang === 'de') {
      this.nzI18nService.setLocale(de_DE);
    }
  }


  // Toggle theme and save to local storage
  toggleTheme() {
    this.theme = !this.theme;
    this.themeChange.emit(this.theme);
  }

  loadProfilePicture(): void {
    this.companyService.getCompanyDetails().subscribe((company) => {
      if (company) {
        this.userName = company.name;
        // Secure logo fetch
        this.companyService.getCompanyLogoBlob().subscribe({
          next: (blob) => {
            if (!blob) {
              console.warn('No logo found, using fallback.');
              this.userProfilePic = 'assets/caretaker-example.jpg';
              return;
            }
            const reader = new FileReader();
            reader.onload = () => {
              this.userProfilePic = reader.result as string;
            };
            reader.readAsDataURL(blob);
          },
          error: (err) => {
            console.error('Unexpected error fetching company logo:', err);
            this.userProfilePic = 'assets/caretaker-example.jpg';
          }
        });
      }
    }, (err) => {
      console.error('Error fetching company details:', err);
      this.userProfilePic = 'assets/caretaker-example.jpg';
    });
  }

  // Logout method
  logout(): void {
    void this.userTourService.terminateActiveTour();
    this.authService.logout(); // Call logout from AuthService
    // this.router.navigate(['/login']); // Navigate to login page after logout
  }

  openHelpDrawer(): void {
    this.isHelpDrawerVisible = true;
  }

  closeHelpDrawer(): void {
    this.isHelpDrawerVisible = false;
  }

  startProductTour(): void {
    if (this.productTourLaunchPending) {
      return;
    }

    this.productTourLaunchPending = true;
    this.waitForHelpDrawerToClose();

    if (this.isHelpDrawerVisible) {
      this.closeHelpDrawer();
    }
  }

  private waitForHelpDrawerToClose(): void {
    if (!this.productTourLaunchPending) {
      return;
    }

    const wrapper = document.querySelector<HTMLElement>(this.helpDrawerWrapperSelector);
    if (!wrapper) {
      this.launchPendingProductTour();
      return;
    }

    if (this.helpDrawerCloseObserver) {
      return;
    }

    this.watchHelpDrawerTransition(wrapper);
    this.helpDrawerCloseObserver = new MutationObserver(() => {
      const currentWrapper = document.querySelector<HTMLElement>(this.helpDrawerWrapperSelector);
      if (!currentWrapper) {
        this.helpDrawerCloseTransitionCompleted = true;
      } else {
        this.watchHelpDrawerTransition(currentWrapper);
        if (!this.isHelpDrawerTransitionEnabled(currentWrapper) && !this.isHelpDrawerVisible) {
          this.helpDrawerCloseTransitionCompleted = true;
        }
      }
      this.tryLaunchPendingProductTour();
    });
    this.helpDrawerCloseObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style']
    });
  }

  private readonly onHelpDrawerTransitionEnd = (event: TransitionEvent): void => {
    if (
      event.target !== this.helpDrawerTransitionTarget ||
      event.propertyName !== 'transform'
    ) {
      return;
    }

    this.helpDrawerCloseTransitionCompleted = true;
    this.tryLaunchPendingProductTour();
  };

  private watchHelpDrawerTransition(wrapper: HTMLElement): void {
    if (wrapper === this.helpDrawerTransitionTarget) {
      return;
    }

    this.helpDrawerTransitionTarget?.removeEventListener('transitionend', this.onHelpDrawerTransitionEnd);
    this.helpDrawerTransitionTarget = wrapper;
    wrapper.addEventListener('transitionend', this.onHelpDrawerTransitionEnd);
  }

  private isHelpDrawerTransitionEnabled(wrapper: HTMLElement): boolean {
    return getComputedStyle(wrapper)
      .transitionDuration
      .split(',')
      .some((duration) => Number.parseFloat(duration) > 0);
  }

  private tryLaunchPendingProductTour(): void {
    if (!this.productTourLaunchPending || !this.isHelpDrawerFullyClosed()) {
      return;
    }

    this.launchPendingProductTour();
  }

  private isHelpDrawerFullyClosed(): boolean {
    if (this.isHelpDrawerVisible || !this.helpDrawerCloseTransitionCompleted) {
      return false;
    }

    const wrapper = document.querySelector<HTMLElement>(this.helpDrawerWrapperSelector);
    if (!wrapper) {
      return true;
    }

    const drawer = wrapper.closest<HTMLElement>('.ant-drawer');
    if (!drawer || drawer.classList.contains('ant-drawer-open') || drawer.querySelector('.ant-drawer-mask')) {
      return false;
    }

    const panelBounds = wrapper.getBoundingClientRect();
    const viewportWidth = document.documentElement.clientWidth;
    const viewportHeight = document.documentElement.clientHeight;
    if (drawer.classList.contains('ant-drawer-left')) {
      return panelBounds.right <= 1;
    }
    if (drawer.classList.contains('ant-drawer-top')) {
      return panelBounds.bottom <= 1;
    }
    if (drawer.classList.contains('ant-drawer-bottom')) {
      return panelBounds.top >= viewportHeight - 1;
    }
    return panelBounds.left >= viewportWidth - 1;
  }

  private clearHelpDrawerCloseWait(): void {
    this.helpDrawerCloseObserver?.disconnect();
    this.helpDrawerCloseObserver = null;
    this.helpDrawerTransitionTarget?.removeEventListener('transitionend', this.onHelpDrawerTransitionEnd);
    this.helpDrawerTransitionTarget = null;
    this.helpDrawerCloseTransitionCompleted = false;
  }

  private launchPendingProductTour(): void {
    if (!this.productTourLaunchPending) {
      return;
    }
    this.productTourLaunchPending = false;
    this.clearHelpDrawerCloseWait();
    void this.userTourService.startManualTour();
  }

  @HostListener('window:resize')
  updateDrawerWidth(): void {
    this.drawerWidth = window.innerWidth <= 768 ? '100%' : '560px';
  }

  openPreview(imageUrl: string): void {
    const images: NzImage[] = [
      {
        src: imageUrl,
        width: '800px',
        height: '600px',
        alt: 'Example image preview'
      }
    ];
    this.nzImageService.preview(images);
  }
}
